import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Clock, Users, Star, DollarSign, CheckCircle } from 'lucide-react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import CityTourBookingForm from '../../components/CityTourBookingForm';

const KampalaComplete = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  const tourDetails = {
    title: 'Complete Kampala Experience',
    price: 25,
    duration: 'Full Day'
  };

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://imgur.com/EzHnSsj.jpg"
            alt="Complete Kampala Experience"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/uganda-city-tours" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to City Tours
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <div className="inline-flex items-center bg-ta-green/20 rounded-full px-2.5 py-0.5 text-xs text-ta-green mb-4 backdrop-blur-sm">
              Best Value
            </div>
            <h1 className="text-4xl font-bold mb-4">Complete Kampala Experience</h1>
            
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex items-center">
                <MapPin className="h-5 w-5 text-ta-green mr-1" />
                <span>Kampala</span>
              </div>
              <div className="flex items-center">
                <Clock className="h-5 w-5 text-ta-green mr-1" />
                <span>Full Day</span>
              </div>
              <div className="flex items-center">
                <Users className="h-5 w-5 text-ta-green mr-1" />
                <span>1-8 people</span>
              </div>
              <div className="flex items-center">
                <Star className="h-5 w-5 text-yellow-400 fill-current mr-1" />
                <span>4.9 (98 reviews)</span>
              </div>
            </div>
            
            <button
              onClick={() => setIsBookingModalOpen(true)}
              className="px-6 py-3 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors"
            >
              Book Now - $25 per person
            </button>
          </div>
        </div>
      </section>

      {/* Tour Details */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Tour Overview</h2>
              <p className="text-gray-600 mb-6">
                Experience the complete essence of Kampala in this comprehensive full-day tour. From historical 
                landmarks to modern developments, immerse yourself in the city's rich culture, vibrant markets, 
                and fascinating history with our expert local guides.
              </p>

              <h3 className="text-xl font-bold text-gray-900 mb-4">Tour Highlights</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-ta-green mr-2 mt-0.5" />
                  <span>Visit all major landmarks</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-ta-green mr-2 mt-0.5" />
                  <span>Traditional lunch included</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-ta-green mr-2 mt-0.5" />
                  <span>Cultural performances</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-ta-green mr-2 mt-0.5" />
                  <span>Local market visits</span>
                </div>
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-4">Detailed Itinerary</h3>
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Morning: Historical Sites</h4>
                  <p className="text-gray-600">
                    Start your day exploring Kampala's historical landmarks, including the Uganda Museum, 
                    Independence Monument, and Kasubi Tombs. Learn about Uganda's rich cultural heritage 
                    and historical significance.
                  </p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Afternoon: Markets & Culture</h4>
                  <p className="text-gray-600">
                    After a traditional lunch, visit the bustling Owino Market and craft markets. 
                    Experience local shopping culture and see traditional crafts being made.
                  </p>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Evening: Modern Kampala</h4>
                  <p className="text-gray-600">
                    End your day exploring modern Kampala, including visits to contemporary shopping 
                    malls and entertainment districts. Optional sunset viewing at a rooftop location.
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Tour Information</h3>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Price</span>
                      <span className="font-semibold text-gray-900">$25 per person</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Duration</span>
                      <span className="font-semibold text-gray-900">Full Day</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Group Size</span>
                      <span className="font-semibold text-gray-900">1-8 people</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Language</span>
                      <span className="font-semibold text-gray-900">English</span>
                    </div>
                  </div>

                  <button
                    onClick={() => setIsBookingModalOpen(true)}
                    className="w-full py-3 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors"
                  >
                    Book Now
                  </button>

                  <div className="mt-6 space-y-3">
                    <div className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">Professional guide</span>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">Traditional lunch</span>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">All entrance fees</span>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">Transport between sites</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Form Modal */}
      <CityTourBookingForm
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        tourTitle={tourDetails.title}
        tourPrice={tourDetails.price}
        tourDuration={tourDetails.duration}
      />

      <Footer />
    </>
  );
};

export default KampalaComplete;